import { FC, useCallback } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import {
  ProgressButton,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { EmployeePermission } from 'src/api/permissions';
import { Loader } from 'src/components/Loader/Loader';
import { Panel } from 'src/components/Panel/Panel';
import { getCurrentDesk, getDesksTimeBounds } from 'src/store/desks/selectors';
import { unbookDesk } from 'src/store/desks/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getUserPermissions, getUserState } from 'src/store/user/selectors';
import { Desk } from 'src/types';

import { DeskBookingForm } from './DeskBookingForm';
import { DeskBookingSidesheetFields } from './DeskBookingSidesheetFields';

export interface DeskBookingFormValues {
  bookedFor: Option<string> | null;
  dates: Set<string>;
}

interface ActionsProps {
  employeeNumber: string;
  permissions: Set<EmployeePermission>;
  bookable: boolean;
  unbookable: boolean;
}

const Actions: FC<ActionsProps> = ({
  employeeNumber,
  permissions,
  bookable,
  unbookable,
}) => {
  const dispatch = useAppDispatch();
  const { submit } = useForm();
  const { error: dateError } = useSelector(getDesksTimeBounds);

  const bookedFor = useField<Option<string> | null>('bookedFor').input.value;

  const book = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');
  }, [submit]);

  const unbook = useCallback(async () => {
    await dispatch(unbookDesk());
  }, [dispatch]);

  if (!permissions.has(EmployeePermission.BookDesks)) return null;

  if (bookable) {
    return (
      <ProgressButton
        variant='contained'
        onClick={book}
        fullWidth
        data-testid='bookDesk'
        disabled={!!dateError}
      >
        {bookedFor && bookedFor.id !== employeeNumber
          ? 'Забронировать на коллегу'
          : 'Забронировать'}
      </ProgressButton>
    );
  }

  if (unbookable) {
    return (
      <ProgressButton
        variant='outlined'
        onClick={unbook}
        fullWidth
        data-testid='unbookDesk'
      >
        Отменить бронь
      </ProgressButton>
    );
  }

  return null;
};

interface InnerProps {
  desk: Desk;
  employeeNumber: string;
  permissions: Set<EmployeePermission>;
}

const Inner: FC<InnerProps> = ({ desk, employeeNumber, permissions }) => {
  const { data } = useSelector(getUserState);

  const bookable =
    !desk.responsibleEmployeeNumber &&
    !desk.status &&
    (!desk.spaceId ||
      data?.spaces.has(desk.spaceId) ||
      permissions.has(EmployeePermission.BookForOthers));

  const unbookable =
    !desk.responsibleEmployeeNumber &&
    !!desk.status &&
    (desk.status.responsibleEmployeeNumber === employeeNumber ||
      permissions.has(EmployeePermission.EditPlans));

  return (
    <DeskBookingForm desk={desk}>
      <Panel
        actions={
          <Actions
            employeeNumber={employeeNumber}
            permissions={permissions}
            bookable={bookable}
            unbookable={unbookable}
          />
        }
      >
        <DeskBookingSidesheetFields
          desk={desk}
          permissions={permissions}
          bookable={bookable}
        />
      </Panel>
    </DeskBookingForm>
  );
};

export const DeskBookingSidesheetPanel: FC = () => {
  const desk = useSelector(getCurrentDesk);
  const data = useSelector(getUserPermissions);

  if (!data?.employeeNumber) return null;

  if (!desk) return <Loader />;

  return (
    <Inner
      key={desk.mapPlaceId}
      desk={desk}
      employeeNumber={data.employeeNumber}
      permissions={data.permissions}
    />
  );
};
